import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import {
  CONTACT_COLUMNS, TAG_JOINER, buildCsv, contactRow, downloadCsv, parseContactsCsv,
} from '../../csv';
import './MemberHome.css';

/* "Manage our company's Bravos Members" — the company roster, on its own screen because it grows
   large (the home carries only the headcount tile).

   Two kinds of row, deliberately in ONE list: colleagues who are actually here, and addresses the
   admin uploaded who are not here yet. Splitting them into two screens would hide the second half
   of the job, which is inviting them.

   ★ UPLOADING IS NOT INVITING. A CSV row is an unverified claim, so it is staged and nothing
   more — no address is looked up against the person table and nobody is created — and the result
   comes back as a total, never per address. Anything else turns a file of guessed addresses into
   a directory of strangers. The person is created when they accept the signed invite.

   ★ THE LIST IS A LIST. Everything you do TO one person — their Labs, reclaiming a seat — lives
   on that person's own screen, one tap away. A few hundred colleagues each carrying their seats
   and a button per seat is not a roster you can read, and it was heavy to render besides.

   Tags are the company's own way of slicing its people ("leader", "ops", "antwerp office"). They
   ride along from the CSV, work the same on both kinds of row, and drive both the filter here and
   the leader denominators on the journey.

   ★ A TAG HAS A LOOK, AND THE FILTER STRIP IS WHERE IT IS GIVEN ONE. A few hundred people wearing
   identical grey chips is a list you have to read word by word; a colour and an icon make it one
   you can scan. Editing lives on the filter strip and nowhere else, because that is the only
   place the whole vocabulary is visible at once — editing a tag from a row would mean finding
   somebody who happens to wear it first. "+ Tag" there DECLARES one with nobody on it yet, which
   is how an admin sets the vocabulary up before tagging into it.

   ★ FACES BEFORE NAMES. A roster is scanned for a person, and a photo is recognised faster than
   a name is read. A staged row has no account and so no photo — it gets initials, which is also
   the quietest way the two kinds of row stay told apart.

   ★★ HOUSES ARE MANAGED HERE, and invented on /programmes. The two are not the same job: naming
   the four divisions a company stands for is a decision taken once, and putting three hundred
   people into them is a list-shaped chore — filter, tick, assign, upload a spreadsheet. So the
   Houses tab over there keeps the shelf and counts what is on it, and links here for the people.
   The house rides the SAME csv as the tags, for the same reason: one roster file, exported and
   brought back, never two that have to agree about who exists.

   ⚠️ A house works on both kinds of row, but it lands in two different places — a colleague who
   has joined gets a data.HouseMember row, an uploaded address parks its house on the roster blob
   until the invite is accepted. The screen does not show that difference anywhere, deliberately;
   the API sorts it out. */

// Mirrors MEMBER_IMPORT_MAX on the API — a bigger file is trimmed here (and said so) rather
// than bounced by the server after the admin already picked it.
const IMPORT_MAX = 1000;

// One datalist for every "add a tag" box on the screen: typing re-uses the vocabulary instead
// of quietly growing a second near-copy of a tag that already exists.
const TAG_OPTIONS_ID = 'bravos-tag-options';

// "Nobody's house yet" as a filter value. A sentinel rather than '' because '' already means
// "don't filter at all", and the people still to place are the whole reason to filter by house.
const NO_HOUSE = '__none__';
// The same for the org chart: "placed in no unit yet".
const NO_UNIT = '__none__';

const HEX = /^#[0-9a-f]{6}$/i;

/* The events a row takes part in: a Lab seat (Bravos) or a place on one of the company's teams
   (Hercules Trophy, Pink Ladies Games). The API sends them lower-cased as `event_ids`; the seats
   are the fallback for a payload from before it did. */
const eventIdsOf = (row) => (row.event_ids
  || (row.allocations || []).map((a) => String(a.event_id).toLowerCase()));

/* A tag's colour, lightened towards white. Mixed here rather than in CSS with color-mix() so a
   chip looks the same in every browser this screen has to work in — and so a tag with NO colour
   keeps exactly the grey chip it always had, untouched. */
const mixWhite = (color, amount) => {
  const n = parseInt(color.slice(1), 16);
  const mix = (v) => Math.round(v + (255 - v) * amount);
  /* eslint-disable no-bitwise */
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
  /* eslint-enable no-bitwise */
};

/* The three shades one chip needs. Undefined for a colourless tag, which is what leaves the
   stylesheet in charge of it. */
const tagStyle = (color, on) => {
  if (!HEX.test(color || '')) return undefined;
  return on
    ? { background: color, borderColor: color, color: '#fff' }
    : { background: mixWhite(color, 0.88), borderColor: mixWhite(color, 0.6), color };
};

/* A blob URL with a space in it breaks the CSS url() it is dropped into. Same guard the home
   strip uses for a value's picture. */
const cssUrl = (image) => (
  typeof image === 'string' && image.includes(' ') ? encodeURI(image) : image);

/* Up to two letters, for somebody with no photo. */
const initials = (text) => (text || '?')
  .trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0))
  .join('') || '?';

/* The face, or the initials in its place. A row with no photo is the normal case — a staged
   colleague has no account to hang one on — so the fallback has to look deliberate. */
const Avatar = ({ name, email, photoUrl }) => (photoUrl ? (
  <img className="bravos-member__avatar" src={photoUrl} alt="" />
) : (
  <span className="bravos-member__avatar bravos-member__avatar--empty">
    {initials(name || email)}
  </span>
));

/* One tag, as a chip. On a row it can be removed; as a filter it toggles, carries its count and
   offers the pencil that opens the editor. */
const Tag = ({ label, meta, count, onRemove, onClick, on, onEdit }) => (
  <span
    className={`bravos-tag ${onClick ? 'bravos-tag--filter' : ''} ${on ? 'bravos-tag--on' : ''}`}
    style={tagStyle(meta && meta.color, on)}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
  >
    {meta && meta.icon && <Icon name={meta.icon} className="bravos-tag__icon" />}
    {label}
    {count != null && <span className="bravos-tag__n">{count}</span>}
    {onEdit && (
      <button
        type="button"
        className="bravos-tag__x bravos-tag__edit"
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
      >
        <Icon name="pencil" className="bravos-tag__icon" />
      </button>
    )}
    {onRemove && (
      <button type="button" className="bravos-tag__x" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
        ×
      </button>
    )}
  </span>
);

/* The tag strip on one row: the chips, plus an inline input behind a "+". */
const RowTags = ({ tags, meta, onSet, disabled }) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState('');

  const commit = () => {
    const tag = value.trim();
    setValue('');
    setAdding(false);
    if (tag && !tags.includes(tag.toLowerCase())) onSet([...tags, tag]);
  };

  return (
    <div className="bravos-tags">
      {tags.map((tag) => (
        <Tag
          key={tag}
          label={tag}
          meta={meta[tag]}
          onRemove={disabled ? undefined : () => onSet(tags.filter((x) => x !== tag))}
        />
      ))}
      {adding ? (
        <input
          className="bravos-taginput"
          autoFocus
          list={TAG_OPTIONS_ID}
          value={value}
          maxLength={40}
          placeholder={t('addTagPlaceholder')}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setValue(''); setAdding(false); }
          }}
        />
      ) : (
        <button
          type="button"
          className="bravos-tag__add"
          onClick={() => setAdding(true)}
          disabled={disabled}
        >
          + {t('addTag')}
        </button>
      )}
    </div>
  );
};

/* The house on one row: what it is now, and the picker that changes it.

   ★ A select and not a chip with a ×. A person is in exactly ONE house at a time, so the gesture
   is "change it to", never "add another" — and a dropdown that already holds the four names is
   also the shortest way to say what the four names are to an admin who is halfway down a list.

   The swatch carries the house's colour, or its picture when it has one: the same face it wears
   on /programmes and on the home strip, so the four houses are recognised the same way
   everywhere. */
const HouseCell = ({ houses, value, disabled, onSet }) => {
  const { t } = useTranslation();
  const current = houses.find((h) => h.id === value) || null;
  return (
    <label className="bravos-housecell">
      {/* backgroundColor + backgroundImage, never the `background` shorthand — the shorthand
          would wipe the centre/cover/no-repeat the stylesheet sets and a picture would tile. */}
      <span
        className="bravos-housecell__dot"
        style={current
          ? { backgroundColor: current.color || '#5a6472',
              backgroundImage: current.image ? `url("${cssUrl(current.image)}")` : undefined,
              borderStyle: 'solid',
              borderColor: 'rgba(0, 35, 53, 0.12)' }
          : undefined}
      />
      <select
        value={value || ''}
        disabled={disabled}
        onChange={(e) => onSet(e.target.value || null)}
      >
        <option value="">{t('noHouse')}</option>
        {houses.map((h) => (
          <option key={h.id} value={h.id}>{h.name}</option>
        ))}
      </select>
    </label>
  );
};

/* The tag editor: one tag's name, colour and icon, opened from the filter strip.

   It is a panel and not a modal because it belongs to the strip above it — you edit a tag while
   looking at the tags around it, which is how you notice that 'ops' and 'operations' are the
   same thing. Deleting is here rather than on a chip's own ×: on a chip that gesture means
   "off this person", and the same symbol meaning "off everybody" one row up is how a roster
   loses a tag it meant to keep. */
const TagEditor = ({ tag, count, palette, icons, busy, onSave, onDelete, onCancel }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(tag ? tag.tag : '');
  const [color, setColor] = useState((tag && tag.color) || '');
  const [icon, setIcon] = useState((tag && tag.icon) || '');
  const isNew = !tag;

  const save = () => {
    const clean = name.trim();
    if (!clean) return;
    onSave({ tag: isNew ? clean : tag.tag,
             rename_to: isNew ? undefined : clean,
             color: color || null,
             icon: icon || null });
  };

  return (
    <div className="bravos-tagedit">
      <div className="bravos-tagedit__head">
        <span className="bravos-form__label mb-0">{isNew ? t('newTag') : t('editTag')}</span>
        {/* What it will look like, next to the fields that decide it — a colour picked against
            a swatch and a colour seen as the chip itself are not the same judgement. */}
        <Tag label={name.trim() || t('addTagPlaceholder')} meta={{ color, icon }} />
      </div>

      <input
        type="text"
        className="bravos-jname"
        value={name}
        maxLength={40}
        autoFocus
        placeholder={t('addTagPlaceholder')}
        onChange={(e) => setName(e.target.value)}
      />

      <p className="bravos-form__label mt-2">{t('tagColour')}</p>
      <div className="bravos-tagedit__swatches">
        {/* "No colour" first: it is the state every existing tag is in, so it has to be
            reachable back — a palette you can only ever add to has no undo. */}
        <button
          type="button"
          className={`bravos-swatch bravos-swatch--none ${!color ? 'is-on' : ''}`}
          title={t('tagNoColour')}
          onClick={() => setColor('')}
        >
          <Icon name="cross-small" />
        </button>
        {palette.map((c) => (
          <button
            key={c}
            type="button"
            className={`bravos-swatch ${color === c ? 'is-on' : ''}`}
            style={{ background: c }}
            aria-label={c}
            onClick={() => setColor(c)}
          />
        ))}
        {/* Anything the palette does not have — a brand colour, usually. */}
        <label className="bravos-swatch bravos-swatch--custom" title={t('tagCustomColour')}>
          <Icon name="plus" />
          <input
            type="color"
            value={HEX.test(color) ? color : '#1d4ed8'}
            onChange={(e) => setColor(e.target.value.toLowerCase())}
          />
        </label>
      </div>

      <p className="bravos-form__label mt-2">{t('tagIcon')}</p>
      <div className="bravos-tagedit__icons">
        <button
          type="button"
          className={`bravos-iconpick ${!icon ? 'is-on' : ''}`}
          title={t('tagNoIcon')}
          onClick={() => setIcon('')}
        >
          <Icon name="cross-small" />
        </button>
        {icons.map((name_) => (
          <button
            key={name_}
            type="button"
            className={`bravos-iconpick ${icon === name_ ? 'is-on' : ''}`}
            aria-label={name_}
            onClick={() => setIcon(name_)}
          >
            <Icon name={name_} />
          </button>
        ))}
      </div>

      <div className="bravos-tagedit__actions">
        <button
          type="button"
          className="bravos-copy"
          onClick={save}
          disabled={busy || !name.trim()}
        >
          <Icon name="check" /> {t('save')}
        </button>
        <button type="button" className="bravos-tag__add" onClick={onCancel} disabled={busy}>
          {t('cancel')}
        </button>
        {!isNew && (
          <button
            type="button"
            className="bravos-member__remove bravos-tagedit__delete"
            onClick={onDelete}
            disabled={busy}
            title={t('deleteTag')}
          >
            <Icon name="trash" />
          </button>
        )}
      </div>
      {!isNew && (
        <span className="bravos-netform__hint">{t('deleteTagHint', { n: count })}</span>
      )}
    </div>
  );
};

const CompanyMembersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  // The result of a SEND, kept apart from `notice` because it renders somewhere else: down at
  // the button that caused it. {text, warn}.
  const [sendNotice, setSendNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  // The shareable admin-invite link is minted on demand (one click), then copied with a warm
  // message so it drops straight into WhatsApp/email — same gesture as the Lab invite links.
  const [inviteLink, setInviteLink] = useState(null);
  const [minting, setMinting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [consent, setConsent] = useState(false);
  // "I edited the file and I am uploading it again" — a known address then takes the file's tags
  // instead of merely gaining them, which is the only way to REMOVE a tag from a spreadsheet.
  const [updateExisting, setUpdateExisting] = useState(false);
  const [filters, setFilters] = useState([]);
  const [eventFilter, setEventFilter] = useState('');
  // '' = every house, or a house id, or NO_HOUSE for the people still to place — which is the
  // one the admin actually reaches for, and the reason this is not just a list of houses.
  const [houseFilter, setHouseFilter] = useState('');
  // A unit id (that unit AND everything under it), NO_UNIT, or '' for no filter.
  const [unitFilter, setUnitFilter] = useState('');
  // The house the bulk bar will put the ticked rows into. '' is a real choice here (take them
  // out of their house), so it needs its own "nothing picked yet" and that is null.
  const [bulkHouse, setBulkHouse] = useState(null);
  // Which tag the editor is open on: the catalog entry, or `true` for a tag being declared.
  // Null closes it; only ever one at a time, because it renders inside the strip it edits.
  const [editingTag, setEditingTag] = useState(null);
  const [picked, setPicked] = useState([]);
  const [bulkValue, setBulkValue] = useState('');

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/members')
      .then((res) => setData(res))
      .catch(() => navigate('/member-home'))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  /* People who asked to join through a team or community link and are waiting for a yes. They
     lived only on /manage-employees, which every brand's admin home now skips — so they are
     here, at the top, where the admin already comes to manage people. Accepting provisions the
     person (and puts them on the team the link was for); nobody gets access before that. */
  const [pending, setPending] = useState([]);
  const loadPending = useCallback(() => {
    apiRequest('GET', '/join-requests')
      .then((res) => setPending((res && res.requests) || []))
      .catch(() => setPending([]));
  }, []);
  useEffect(() => { loadPending(); }, [loadPending]);

  const answerJoin = (id, accept) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', accept ? '/accept-join' : '/decline-join', { request_id: id })
      .then(() => { loadPending(); if (accept) load(); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  // "Remove" doesn't delete the person — the backend ends their employee/admin role and
  // files them as an internal 'affiliate', so they drop off this roster but stay known.
  const removeMember = (m) => {
    if (busy) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('removeMemberConfirm', { name: m.name }))) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/bravos/members/remove', { partner_id: m.id })
      .then(() => { setNotice(t('memberRemoved', { name: m.name })); load(); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('removeMemberFailed') })))
      .finally(() => setBusy(false));
  };

  const copyInvite = (invite) => {
    // "…on Bravos" is only true on Bravos; everywhere else the brand names itself.
    const line = data && data.is_bravos === false
      ? t('adminInviteShareTextBrand', {
        company: invite.company_name || '', brand: (data.brand && data.brand.name) || '' })
      : t('adminInviteShareText', { company: invite.company_name || '' });
    const text = `${line}\n${invite.link}`;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done();
    }
  };

  const inviteAdmin = () => {
    if (minting) return;
    // Already minted -> the button just copies again (no second token for the same gesture).
    if (inviteLink) {
      copyInvite(inviteLink);
      return;
    }
    setMinting(true);
    setNotice(null);
    apiRequest('GET', '/bravos/admin-invite-link')
      .then((res) => { setInviteLink(res); copyInvite(res); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setMinting(false));
  };

  const setTags = (id, tags) => {
    if (busy) return;
    setBusy(true);
    apiRequest('POST', '/bravos/members/tags', { id, tags })
      .then(load)
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  /* Put one row, or every row that is ticked, into a house. One call for both — the API takes
     `ids` and sorts out which of them are colleagues and which are addresses still waiting.

     ★ It reloads rather than patching the row in place: the house counts on the filter strip
     move with every assignment, and a strip that lies about how many people are in Green is a
     strip nobody trusts twice. */
  const setHouse = (ids, houseId) => {
    if (busy || !ids.length) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/bravos/members/house', { ids, house_id: houseId || '' })
      .then((res) => {
        if (ids.length > 1) {
          setNotice(t('housesAssigned', { n: (res && res.changed) || 0 }));
          setPicked([]);
          setBulkHouse(null);
        }
        load();
      })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  /* Declare a tag, rename one, or repaint one — one call, because on the API side a rename has
     to move the word across every row in the same write that changes its look. */
  const saveTag = (body) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/bravos/members/tag', body)
      .then((res) => {
        setEditingTag(null);
        // A renamed tag takes the filter with it. Without this, filtering by 'leader' and then
        // renaming it leaves the strip pointing at a word that no longer exists — an empty
        // list, for a reason that is no longer on the screen. The API's name is the one to
        // follow: what the admin typed is trimmed and lower-cased on the way in.
        const saved = res && res.tag && res.tag.tag;
        if (saved && saved !== body.tag) {
          setFilters((f) => f.map((x) => (x === body.tag ? saved : x))
            .filter((x, i, all) => all.indexOf(x) === i));
        }
        load();
      })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  /* Take a tag off the company altogether. It asks first and says how many people are wearing
     it — "delete 'leader'" is a different decision when 40 colleagues carry it than when 0 do,
     and the roster itself gives no warning because nobody is removed by this. */
  const deleteTag = (tag, count) => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('deleteTagConfirm', { tag, n: count }))) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/bravos/members/tag/delete', { tag })
      .then((res) => {
        setEditingTag(null);
        // The filter cannot keep pointing at a tag that no longer exists, or the list stays
        // empty for a reason that is no longer on the screen.
        setFilters((f) => f.filter((x) => x !== tag));
        setNotice(t('tagDeleted', { tag, n: (res && res.changed) || 0 }));
        load();
      })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  const bulkTag = (mode) => {
    const tag = bulkValue.trim();
    if (busy || !tag || !picked.length) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/bravos/members/tags', {
      ids: picked,
      add: mode === 'add' ? [tag] : [],
      remove: mode === 'remove' ? [tag] : [],
    })
      .then((res) => {
        setNotice(t('tagsUpdated', { n: res.changed }));
        setBulkValue('');
        setPicked([]);
        load();
      })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  /* Read the CSV here, post the rows, reload. Nothing is mailed and nobody is created — the
     rows land as pending and the admin invites whoever they mean to. */
  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';                       // so picking the same file twice still fires
    if (!file || busy) return;
    setNotice(null);
    const reader = new FileReader();
    reader.onload = () => {
      const all = parseContactsCsv(reader.result);
      if (!all.length) { setNotice(t('contactsNoneFound')); return; }
      const members = all.slice(0, IMPORT_MAX);
      setBusy(true);
      apiRequest('POST', '/bravos/members/import',
        { members, consent: true, update_existing: updateExisting })
        .then((r) => {
          const parts = [t('membersStaged', { n: (r && r.saved) || 0 })];
          if (r && r.updated) parts.push(t('membersUpdated', { n: r.updated }));
          if (r && r.skipped) parts.push(t('contactsSkipped', { n: r.skipped }));
          // What the House column did, said separately from what the addresses did: they are two
          // jobs in one file and an admin who filled the column in wants to know it landed.
          if (r && r.housed) parts.push(t('membersHoused', { n: r.housed }));
          // ★ Named, not counted. "3 house names were not recognised" sends somebody hunting
          // through two hundred rows; the names themselves are usually the typo, visible.
          if (r && (r.unknown_houses || []).length) {
            parts.push(t('housesNotRecognised', { names: r.unknown_houses.join(', ') }));
          }
          if (r && r.full) parts.push(t('contactsListFull', { max: IMPORT_MAX }));
          if (all.length > members.length) parts.push(t('contactsTruncated', { max: IMPORT_MAX }));
          setNotice(parts.join(' '));
          load();
        })
        .catch((err) => setNotice(t(err.message, { defaultValue: t('somethingWentWrong') })))
        .finally(() => setBusy(false));
    };
    reader.onerror = () => setNotice(t('somethingWentWrong'));
    reader.readAsText(file);
  };

  /* The empty file, with the column names the parser reads by name and one example row, so
     "what does it want?" is answered by opening it rather than by reading a hint.

     ★ The example House is one of the company's OWN, when it has any — the column is matched on
     the name and nothing else, so a template showing a house that does not exist teaches exactly
     the mistake the upload then refuses. */
  const downloadTemplate = () => downloadCsv(`${filePrefix}members-template.csv`, buildCsv([
    CONTACT_COLUMNS,
    ['Alex', 'Example', 'alex@example.com', ['leader', 'ops'].join(TAG_JOINER),
     (houses[0] && houses[0].name) || ''],
  ]));

  /* Mail the invite to these staged rows. ``force`` decides whether the API's once-a-day guard
     applies: without it an address already mailed today is refused, so a second click on a mass
     send cannot mail the same colleague twice in an afternoon. A hand-ticked send and one
     person's "Invite again" are the deliberate cases and always go.

     ★ The answer lands in ``sendNotice``, which renders BESIDE the send button — not in the card
     at the top of the screen, where it used to. The roster is long, so an admin who clicks at
     the bottom of it never saw the reply painted above the fold: "0 sent, 1 already had an
     invitation today" read as "the button did nothing". */
  const invitePending = (ids, { force = false, clearPicked = false } = {}) => {
    if (busy || !ids.length) return;
    setBusy(true);
    setNotice(null);
    setSendNotice(null);
    apiRequest('POST', '/bravos/members/invite', { ids, force })
      .then((r) => {
        // Four numbers, because they mean four different things and only one of them means an
        // invitation is on its way.
        const sent = (r && r.sent) || 0;
        const parts = [t('invitesSent', { n: sent })];
        if (r && r.skipped) parts.push(t('invitesSkipped', { n: r.skipped }));
        if (r && r.failed) parts.push(t('invitesFailed', { n: r.failed }));
        if (r && r.too_soon) parts.push(t('invitesTooSoon', { n: r.too_soon }));
        // Warn tone whenever fewer mails went out than rows were asked for — that is the half
        // of the answer the admin has to notice.
        setSendNotice({ text: parts.join(' '), warn: sent < ids.length });
        if (clearPicked) setPicked([]);
        load();
      })
      .catch((err) => setSendNotice({
        text: t(err.message, { defaultValue: t('somethingWentWrong') }),
        warn: true,
      }))
      .finally(() => setBusy(false));
  };

  /* Mail the rows you ticked. It is one button and not two, because "invite" and "re-invite"
     are the same send — the difference is only whether these people have heard from us before,
     which the rows themselves know. When some of them have, it says so and asks first: it is the
     one button on the screen that mails a colleague a second time.

     It replaced a "re-invite all" button, which could only ever mean the whole list. Ticking the
     top row is the same gesture, with a way to leave three names out of it.

     ★ It sends ``force``, so the once-a-day guard does not apply here. Ticking a name is as
     deliberate as opening that row and pressing "Invite again", and while the guard held, the
     deliberate case was the one thing this button could not do: you ticked one colleague,
     confirmed the dialog, and nothing was mailed. What protects the roster instead is the
     confirm below — it says how many are getting a second mail — and the mass button beside it,
     which still only ever mails the never-invited. The one gesture that can now mail 240 people
     twice in a day is the one where you ticked 240 boxes and then read a dialog saying so. */
  const inviteSelected = (rows) => {
    const ids = rows.map((r) => r.id);
    const again = rows.filter((r) => r.invited_at).length;
    if (busy || !ids.length) return;
    // eslint-disable-next-line no-alert
    if (again && !window.confirm(t('reinviteSelectedConfirm', { n: ids.length, again }))) return;
    invitePending(ids, { force: true, clearPicked: true });
  };

  /* What one pending row says about itself. "Invited 12 Aug" is a date; after the third mail
     the number is the more useful half, because it is the one that says stop. */
  const pendingLabel = (row) => {
    if (!row.invited_at) return t('notInvitedYet');
    const n = row.invite_count || 1;
    return n > 1
      ? t('invitedOnTimes', { date: row.invited_at, n })
      : t('invitedOn', { date: row.invited_at });
  };

  const dropStaged = (row) => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('removeMemberConfirm', { name: row.email }))) return;
    setBusy(true);
    apiRequest('DELETE', `/bravos/members/staged/${row.id}`)
      .then(load)
      .catch((err) => setNotice(t(err.message, { defaultValue: t('somethingWentWrong') })))
      .finally(() => setBusy(false));
  };

  const toggleFilter = (tag) => setFilters((f) => (
    f.includes(tag) ? f.filter((x) => x !== tag) : [...f, tag]));

  const togglePick = (id) => setPicked((p) => (
    p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const brand = data && data.brand;
  const selfId = data && data.self_id;
  // ★ Every brand's company admin lands here since 2026-10-18. Only Bravos sells a membership and
  // Labs, so only Bravos gets the "Bravos Members" title, the Member / Not yet badge and the Lab
  // copy; a Hercules Trophy, Pink Ladies Games or Fabrica company sees its colleagues (or, on a
  // community, its members).
  const isBravos = !data || data.is_bravos !== false;
  const isCommunity = Boolean(data && data.is_community);
  const filePrefix = isBravos ? 'bravos-' : '';
  // The vocabulary, each entry carrying its look. It includes tags nobody wears yet — declaring
  // one is how the strip is set up before anybody is tagged into it.
  const tagCatalog = useMemo(() => (data && data.tags) || [], [data]);
  const allTags = useMemo(() => tagCatalog.map((c) => c.tag), [tagCatalog]);
  const tagMeta = useMemo(() => Object.fromEntries(
    tagCatalog.map((c) => [c.tag, { color: c.color, icon: c.icon }]),
  ), [tagCatalog]);
  const tagIcons = (data && data.tag_icons) || [];
  const tagColors = (data && data.tag_colors) || [];
  const allEvents = (data && data.events) || [];
  const allMembers = useMemo(() => (data && data.members) || [], [data]);
  const allStaged = useMemo(() => (data && data.staged) || [], [data]);
  // Empty for a company that has no houses — which is what keeps the column, the filter and the
  // bulk bar off the screen entirely rather than showing three empty controls.
  const houses = useMemo(() => (data && data.houses) || [], [data]);
  const houseNames = useMemo(
    () => Object.fromEntries(houses.map((h) => [h.id, h.name])), [houses],
  );
  // The org tree in walk order, each unit with its depth. Empty when nobody is placed, which
  // keeps the unit filter off the screen for a company without a structure.
  const units = useMemo(() => (data && data.units) || [], [data]);
  /* Unit id -> every unit id at or under it. The API sends the tree pre-order, so a unit's
     subtree is simply the run of rows after it that sit deeper — picking a division finds the
     people in its departments too, which is what "show me Medical" means. */
  const unitSubtree = useMemo(() => {
    const out = {};
    units.forEach((u, i) => {
      const ids = new Set([u.id.toLowerCase()]);
      for (let j = i + 1; j < units.length && units[j].depth > u.depth; j += 1) {
        ids.add(units[j].id.toLowerCase());
      }
      out[u.id] = ids;
    });
    return out;
  }, [units]);

  // A row matches when it carries ANY of the ticked tags — an admin filtering by "ops" and
  // "sales" is asking for both teams, not for the people who are somehow in both. The event
  // filter is an AND on top: "of these, the ones sitting in that Lab", and so are the house and
  // the unit.
  const matches = useCallback((row) => {
    if (filters.length && !filters.some((f) => (row.tags || []).includes(f))) return false;
    if (houseFilter === NO_HOUSE) {
      if (row.house_id) return false;
    } else if (houseFilter && row.house_id !== houseFilter) return false;
    if (unitFilter) {
      const unitId = row.unit ? String(row.unit.id).toLowerCase() : null;
      if (unitFilter === NO_UNIT) {
        if (unitId) return false;
      } else if (!unitId || !(unitSubtree[unitFilter] || new Set()).has(unitId)) return false;
    }
    if (!eventFilter) return true;
    // An uploaded address holds no seat and plays in no team, so an event filter is a question it
    // cannot answer yes to.
    return eventIdsOf(row).includes(eventFilter);
  }, [filters, eventFilter, houseFilter, unitFilter, unitSubtree]);

  const members = useMemo(() => allMembers.filter(matches), [allMembers, matches]);
  // ★ An uploaded address is nobody yet, so it sits in no unit and cannot be placed in one: a
  // unit filter — "not placed yet" included — is a question only a real colleague answers.
  const staged = useMemo(
    () => (unitFilter ? [] : allStaged.filter(matches)), [allStaged, matches, unitFilter],
  );

  /* Ticking rows and inviting them is the one mass send left. It follows the filter on purpose —
     an admin who filtered down to 'antwerp office' means that office, not the roster. A second
     button that mailed only the never-invited stood here and was removed: two sends under each
     other, obeying different rules about who they skipped, read as one send you could not trust. */
  const pickedStaged = useMemo(
    () => allStaged.filter((s) => picked.includes(s.id)), [allStaged, picked],
  );
  // Any ticked row that has already been mailed turns the invite button into a re-invite — same
  // send, different promise to the admin.
  const pickedAlreadyInvited = useMemo(
    () => pickedStaged.some((s) => s.invited_at), [pickedStaged],
  );

  /* The tick box on the top row of the pending list. It follows the filters like every other
     mass action here: "select all" means the rows you are looking at, and it adds to whatever
     you had already ticked elsewhere rather than replacing it. */
  const stagedPickedCount = useMemo(
    () => staged.filter((s) => picked.includes(s.id)).length, [staged, picked],
  );
  const allStagedPicked = staged.length > 0 && stagedPickedCount === staged.length;
  const someStagedPicked = stagedPickedCount > 0 && !allStagedPicked;
  const toggleStagedAll = () => setPicked((p) => {
    const ids = staged.map((s) => s.id);
    return allStagedPicked
      ? p.filter((x) => !ids.includes(x))
      : [...new Set([...p, ...ids])];
  });

  /* The counts beside the filters. Computed here from the payload the screen already holds —
     the API does no extra work for them, which is why they can be there at all. They count the
     WHOLE roster, not the filtered view: a number that moves as you filter is not a guide to
     what filtering would do. */
  const tagCounts = useMemo(() => {
    const out = {};
    [...allMembers, ...allStaged].forEach((row) => {
      (row.tags || []).forEach((tag) => { out[tag] = (out[tag] || 0) + 1; });
    });
    return out;
  }, [allMembers, allStaged]);

  const eventCounts = useMemo(() => {
    const out = {};
    allMembers.forEach((m) => {
      new Set(eventIdsOf(m)).forEach((id) => {
        out[id] = (out[id] || 0) + 1;
      });
    });
    return out;
  }, [allMembers]);

  /* Export exactly what is on screen, in the shape the uploader reads back. That is the whole
     point: edit it in your own spreadsheet and upload it again with "update existing" ticked. */
  const exportFiltered = () => downloadCsv(`${filePrefix}members.csv`, buildCsv([
    CONTACT_COLUMNS,
    ...members.map((m) => contactRow({
      // A real colleague carries ONE display name; split on the last space so the two name
      // columns round-trip into something recognisable rather than everything in one cell.
      first_name: (m.name || '').split(' ').slice(0, -1).join(' ') || m.name || '',
      last_name: (m.name || '').split(' ').slice(-1).join(' ') === m.name
        ? '' : (m.name || '').split(' ').slice(-1).join(' '),
      email: m.email,
      tags: m.tags,
      // ★ The house goes out by NAME, because that is what goes back in. The id would round-trip
      // more safely and be unreadable in the one place this file is meant to be edited.
      house: houseNames[m.house_id] || '',
    })),
    ...staged.map((s) => contactRow({ ...s, house: houseNames[s.house_id] || '' })),
  ]));

  const shown = members.length + staged.length;
  const filtering = filters.length > 0 || !!eventFilter || !!houseFilter || !!unitFilter;

  /* The counts beside the house filter, over the WHOLE roster for the same reason the tag counts
     are — a number that moves as you filter is no guide to what filtering would do. Both kinds of
     row count: an address waiting for its invite is already destined for a house. */
  const houseCounts = useMemo(() => {
    const out = { [NO_HOUSE]: 0 };
    [...allMembers, ...allStaged].forEach((row) => {
      const key = row.house_id || NO_HOUSE;
      out[key] = (out[key] || 0) + 1;
    });
    return out;
  }, [allMembers, allStaged]);

  /* The counts beside the unit filter: everybody in and UNDER each unit, over the whole roster,
     so the number beside a division is what picking it will show. Colleagues only — an uploaded
     address cannot be placed. */
  const unitCounts = useMemo(() => {
    const direct = {};
    let none = 0;
    allMembers.forEach((m) => {
      if (m.unit) {
        const key = String(m.unit.id).toLowerCase();
        direct[key] = (direct[key] || 0) + 1;
      } else none += 1;
    });
    const out = { [NO_UNIT]: none };
    units.forEach((u) => {
      out[u.id] = [...(unitSubtree[u.id] || [])].reduce((n, id) => n + (direct[id] || 0), 0);
    });
    return out;
  }, [allMembers, units, unitSubtree]);

  return (
    <Base activeNav="home">
      {/* One list of suggestions for every "add a tag" box on the screen. */}
      <datalist id={TAG_OPTIONS_ID}>
        {allTags.map((tag) => <option key={tag} value={tag} />)}
      </datalist>
      <Card>
        <div className="bravos-brand">
          {brand && brand.logo_url && (
            <img className="bravos-brand__logo" src={brand.logo_url} alt="" />
          )}
          <div>
            <Title my="0" className="mt-1">
              {isBravos ? t('manageBravosMembers')
                : (isCommunity ? t('manageMembers') : t('manageColleagues'))}
            </Title>
          </div>
          <button
            type="button"
            className="bravos-wallet__link bravos-members__back"
            onClick={() => navigate('/member-home')}
          >
            <Icon name="angle-left" /> {t('back')}
          </button>
        </div>
      </Card>

      {loading ? (
        <Card><span className="auth__spinner" /></Card>
      ) : (
        <>
          {pending.length > 0 && (
            <Card>
              <p className="bravos-form__label">
                {t('pendingRequests')} ({pending.length})
              </p>
              {pending.map((req) => (
                <div key={req.id} className="bravos-member">
                  <div className="bravos-member__row">
                    <Avatar name={req.full_name} email={req.email} />
                    <span className="bravos-member__body">
                      <span className="bravos-member__name">{req.full_name}</span>
                      <span className="bravos-member__email">
                        {req.email}{req.team_name ? ` · ${req.team_name}` : ''}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="bravos-copy"
                      onClick={() => answerJoin(req.id, true)}
                      disabled={busy}
                    >
                      <Icon name="check" /> {t('accept')}
                    </button>
                    <button
                      type="button"
                      className="bravos-member__remove"
                      onClick={() => answerJoin(req.id, false)}
                      disabled={busy}
                      title={t('decline')}
                    >
                      <Icon name="cross" />
                    </button>
                  </div>
                </div>
              ))}
            </Card>
          )}

          <Card>
            <p className="bravos-q__hint mb-2">
              {isBravos ? t('manageMembersNote') : t('manageColleaguesNote')}
            </p>

            {/* Invite a NEW admin by link: one click mints + copies a shareable link. Whoever opens
                it fills in their details and lands as an admin, so a colleague who isn't in the
                system yet can be given company access without knowing their address first. */}
            <div className="bravos-adminvite">
              <div className="bravos-adminvite__body">
                <span className="bravos-adminvite__title">{t('inviteAdminTitle')}</span>
                <span className="bravos-adminvite__note">{t('inviteAdminNote')}</span>
              </div>
              <button
                type="button"
                className="bravos-copy"
                title={t('inviteAdminHint')}
                onClick={inviteAdmin}
                disabled={minting}
              >
                <Icon name={copied ? 'check' : 'paper-plane'} />
                {copied ? t('copied') : t('inviteAdmin')}
              </button>
            </div>
            {inviteLink && (
              <p className="bravos-adminvite__link">{inviteLink.link}</p>
            )}

            {/* Bulk-load the roster from a spreadsheet. The consent tick is the admin confirming
                these are their own colleagues and that we may mail them an invite. */}
            <div className="bravos-netupload">
              <span className="bravos-adminvite__title">{t('importMembersTitle')}</span>
              <span className="bravos-netform__hint">{t('importMembersNote')}</span>
              {/* The tick sits ABOVE the button and gates it, so it reads as the condition for
                  uploading rather than as a stray question underneath one. */}
              <label className="bravos-netbatch__all">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>{t('importMembersConsent')}</span>
              </label>
              <label className="bravos-netbatch__all">
                <input
                  type="checkbox"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                />
                <span>{t('importUpdateExisting')}</span>
              </label>
              <button
                type="button"
                className="bravos-event__claim"
                onClick={() => fileRef.current && fileRef.current.click()}
                disabled={busy || !consent}
              >
                <Icon name="upload" className="me-1" /> {t('uploadCsv')}
              </button>
              <button type="button" className="bravos-tag__add" onClick={downloadTemplate}>
                <Icon name="file-download" className="me-1" /> {t('downloadTemplate')}
              </button>
              <span className="bravos-netform__hint">{t('uploadCsvHint')}</span>
              {/* Only for a company that HAS houses: for anybody else the column is inert and a
                  sentence about it is a feature they have not bought being explained at them. */}
              {houses.length > 0 && (
                <span className="bravos-netform__hint">
                  {t('uploadCsvHouseHint', { names: houses.map((h) => h.name).join(', ') })}
                  {/* The names are matched as written, so the screen that writes them is one click
                      away rather than a menu hunt. */}
                  <br />
                  <a
                    href="/houses"
                    onClick={(e) => { e.preventDefault(); navigate('/houses'); }}
                  >
                    {t('membersManageHouses')} →
                  </a>
                </span>
              )}
              <input
                type="file"
                accept=".csv,text/csv"
                className="d-none"
                ref={fileRef}
                onChange={onFile}
              />
            </div>

            {notice && <p className="bravos-cal__notice">{notice}</p>}
          </Card>

          <Card>
            {/* The tag strip is always here, even on a roster with no tags at all — it is the
                only place a tag can be created, so a screen that hides it when there are none
                is a screen where the first tag can never be made. */}
            <p className="bravos-form__label">{t('filterByTag')}</p>
            <div className="bravos-tags">
              {tagCatalog.map((c) => (
                <Tag
                  key={c.tag}
                  label={c.tag}
                  meta={c}
                  count={tagCounts[c.tag] || 0}
                  on={filters.includes(c.tag)}
                  onClick={() => toggleFilter(c.tag)}
                  onEdit={() => setEditingTag(c)}
                />
              ))}
              <button
                type="button"
                className="bravos-tag__add"
                onClick={() => setEditingTag(true)}
                disabled={busy}
              >
                + {t('newTag')}
              </button>
            </div>
            {allTags.length === 0 && !editingTag && (
              <span className="bravos-netform__hint">{t('noTagsYetHint')}</span>
            )}
            {editingTag && (
              <TagEditor
                key={editingTag === true ? 'new' : editingTag.tag}
                tag={editingTag === true ? null : editingTag}
                count={editingTag === true ? 0 : (tagCounts[editingTag.tag] || 0)}
                palette={tagColors}
                icons={tagIcons}
                busy={busy}
                onSave={saveTag}
                onDelete={() => deleteTag(editingTag.tag, tagCounts[editingTag.tag] || 0)}
                onCancel={() => setEditingTag(null)}
              />
            )}

            {/* Filter by house. Only for a company that HAS houses — they are invented on the
                Houses tab of /programmes, and an empty dropdown here would be a control that
                explains nothing about where to go.

                ★ "Not in a house yet" is in the list and carries its own count, because that is
                the entry an admin actually comes here for: filter to it, tick the top row,
                assign. Without it, filling the last gaps means reading three hundred rows. */}
            {houses.length > 0 && (
              <>
                <p className="bravos-form__label mt-2">{t('filterByHouse')}</p>
                <select
                  className="bravos-jname"
                  value={houseFilter}
                  onChange={(e) => setHouseFilter(e.target.value)}
                >
                  <option value="">{t('allHouses')}</option>
                  {houses.map((h) => (
                    <option key={h.id} value={h.id}>
                      {`${h.name} (${houseCounts[h.id] || 0})`}
                    </option>
                  ))}
                  <option value={NO_HOUSE}>
                    {`${t('noHouseYet')} (${houseCounts[NO_HOUSE] || 0})`}
                  </option>
                </select>
              </>
            )}

            {/* Filter by unit of the org chart. Only for a company with a structure, and indented
                the way /company-structure draws it. A unit includes everything under it.
                ★ "Not placed yet" is there for the same reason as "Not in a house yet": the
                people still to place are what an admin most often comes looking for. */}
            {units.length > 0 && (
              <>
                <p className="bravos-form__label mt-2">{t('filterByUnit')}</p>
                <select
                  className="bravos-jname"
                  value={unitFilter}
                  onChange={(e) => setUnitFilter(e.target.value)}
                >
                  <option value="">{t('osAll')}</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {`${'  '.repeat(u.depth)}${u.name} (${unitCounts[u.id] || 0})`}
                    </option>
                  ))}
                  <option value={NO_UNIT}>
                    {`${t('osUnplacedTitle')} (${unitCounts[NO_UNIT] || 0})`}
                  </option>
                </select>
              </>
            )}

            {allEvents.length > 0 && (
              <>
                <p className="bravos-form__label mt-2">{t('filterByEvent')}</p>
                <select
                  className="bravos-jname"
                  value={eventFilter}
                  onChange={(e) => setEventFilter(e.target.value)}
                >
                  <option value="">{t('allEvents')}</option>
                  {allEvents.map((ev) => (
                    <option key={ev.id} value={ev.id}>
                      {`${ev.name || t('lab')} (${eventCounts[ev.id] || 0})`}
                    </option>
                  ))}
                </select>
              </>
            )}
            {filtering && (
              <button
                type="button"
                className="bravos-tag__add mt-2"
                onClick={() => {
                  setFilters([]); setEventFilter(''); setHouseFilter(''); setUnitFilter('');
                }}
              >
                {t('clearFilter')}
              </button>
            )}

            {/* ★ Beside the filters, not at the foot of the roster. It exports exactly what the
                filters above select, and that sentence only lands while you can still see them —
                at the bottom of two hundred rows it read as "download the page you are on", and
                on a filtered list nobody was sure which of the two it meant. It is also the door
                into the round trip (take it away, edit it, bring it back), and a door nobody
                scrolls to is a door nobody uses.

                Drawn even on an empty result, disabled: a filter that matches nobody is when you
                most want to know the button is still there and simply has nothing to give. */}
            <div className="bravos-members__export mt-3">
              <span className="bravos-netform__hint bravos-members__export-lead">
                {t('exportShownIntro')}
              </span>
              <button
                type="button"
                className="bravos-copy"
                onClick={exportFiltered}
                disabled={shown === 0}
              >
                <Icon name="file-download" /> {t('exportShown', { n: shown })}
              </button>
              <span className="bravos-netform__hint">{t('exportShownHint')}</span>
            </div>
          </Card>

          {picked.length > 0 && (
            <Card>
              <p className="bravos-form__label">{t('selectedCount', { n: picked.length })}</p>
              <div className="bravos-form__row">
                <input
                  type="text"
                  className="bravos-taginput"
                  list={TAG_OPTIONS_ID}
                  value={bulkValue}
                  maxLength={40}
                  placeholder={t('addTagPlaceholder')}
                  onChange={(e) => setBulkValue(e.target.value)}
                />
                <button
                  type="button"
                  className="bravos-copy"
                  onClick={() => bulkTag('add')}
                  disabled={busy || !bulkValue.trim()}
                >
                  {t('addTag')}
                </button>
                <button
                  type="button"
                  className="bravos-copy"
                  onClick={() => bulkTag('remove')}
                  disabled={busy || !bulkValue.trim()}
                >
                  {t('removeTag')}
                </button>
              </div>

              {/* Put everybody you ticked in one house. It is the second half of "filter to the
                  ones with no house, select all" and the reason the filter above carries that
                  entry at all.

                  ★ Picking is one act and applying is the next, rather than assigning the moment
                  the dropdown changes: this moves people in bulk, and a select that fires on its
                  own turns a mis-scroll into forty colleagues in the wrong house. */}
              {houses.length > 0 && (
                <>
                  <p className="bravos-form__label mt-3">{t('setHouseForSelected')}</p>
                  <div className="bravos-form__row">
                    <select
                      className="bravos-jname"
                      value={bulkHouse || ''}
                      onChange={(e) => setBulkHouse(e.target.value || null)}
                    >
                      <option value="">{t('choose')}</option>
                      {houses.map((h) => (
                        <option key={h.id} value={h.id}>{h.name}</option>
                      ))}
                      {/* Taking people back OUT is the same gesture and belongs in the same
                          list — a separate button for it would be one more thing to aim at. */}
                      <option value={NO_HOUSE}>{t('noHouse')}</option>
                    </select>
                    <button
                      type="button"
                      className="bravos-copy"
                      onClick={() => setHouse(picked, bulkHouse === NO_HOUSE ? null : bulkHouse)}
                      disabled={busy || !bulkHouse}
                    >
                      {t('assignHouse')}
                    </button>
                  </div>
                </>
              )}
            </Card>
          )}

          <Card>
            {shown === 0 ? (
              <p className="text-muted mb-0">
                {filtering ? t('noMembersMatch') : t('noMembersYet')}
              </p>
            ) : (
              <>
                {members.map((m) => (
                  <div key={m.id} className="bravos-member">
                    <div className="bravos-member__row">
                      <input
                        type="checkbox"
                        className="bravos-netpick__cb"
                        checked={picked.includes(m.id)}
                        onChange={() => togglePick(m.id)}
                      />
                      {/* The person, not a row of controls: tapping the face or the name opens
                          their own screen, where the seats and the admin actions live. */}
                      <button
                        type="button"
                        className="bravos-member__open"
                        onClick={() => navigate(`/company-members/${m.id}`)}
                      >
                        <Avatar name={m.name} email={m.email} photoUrl={m.photo_url} />
                        <span className="bravos-member__body">
                          <span className="bravos-member__name">{m.name}</span>
                          {m.email && <span className="bravos-member__email">{m.email}</span>}
                        </span>
                      </button>
                      {m.is_admin && (
                        <span className="bravos-member__role">{t('roleAdmin')}</span>
                      )}
                      {isBravos && (
                        <span
                          className={`bravos-cta__tag ${m.is_member ? 'bravos-cta__tag--done' : ''}`}
                        >
                          {m.is_member ? t('memberBadge') : t('notMemberBadge')}
                        </span>
                      )}
                      {selfId && m.id !== selfId && (
                        <button
                          type="button"
                          className="bravos-member__remove"
                          onClick={() => removeMember(m)}
                          disabled={busy}
                          title={t('removeMember')}
                        >
                          <Icon name="trash" />
                        </button>
                      )}
                    </div>
                    <div className="bravos-member__meta">
                      <RowTags
                        tags={m.tags || []}
                        meta={tagMeta}
                        disabled={busy}
                        onSet={(tags) => setTags(m.id, tags)}
                      />
                      {houses.length > 0 && (
                        <HouseCell
                          houses={houses}
                          value={m.house_id}
                          disabled={busy}
                          onSet={(id) => setHouse([m.id], id)}
                        />
                      )}
                      {/* Where they sit in the org chart — the same placement the heatmap counts,
                          one tap from the unit itself. Absent for a company without a tree. */}
                      {m.unit && (
                        <button
                          type="button"
                          className="bravos-member__unit"
                          onClick={() => navigate(`/company-structure/${m.unit.id}`)}
                          title={t('osUnitOf')}
                        >
                          <Icon name="sitemap" />
                          {m.unit.name}
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {staged.length > 0 && (
                  <>
                    {/* A rule across the card: above it the people who are in, below it the
                        addresses still waiting. The heading alone did not separate them. */}
                    <p className="bravos-cal__label bravos-cal__label--past bravos-members__split">
                      {t('notJoinedYet')}
                    </p>
                    {/* The top row of the list: tick it and every pending address below is
                        ticked, untick the two you did not mean, send to what is left. The
                        button that did this in one go could only ever mean everyone, so the
                        ordinary case was the one it could not serve.

                        The send sits HERE, beside the tick box, and not in the selection card
                        far above — that card is about tags, and a button you have to scroll
                        back up to find is a button you re-tick the list for. */}
                    <div className="bravos-netbatch">
                      <div className="bravos-netbatch__row">
                        <label className="bravos-netbatch__all">
                          <input
                            type="checkbox"
                            className="bravos-netpick__cb"
                            checked={allStagedPicked}
                            ref={(el) => {
                              // Half-ticked when you unticked a few: an empty box would read
                              // as "none of these", which is not what you just did.
                              if (el) el.indeterminate = someStagedPicked;
                            }}
                            onChange={toggleStagedAll}
                          />
                          <span>{t('selectAllCount', { n: staged.length })}</span>
                        </label>
                        <button
                          type="button"
                          className="bravos-copy"
                          onClick={() => inviteSelected(pickedStaged)}
                          disabled={busy || !pickedStaged.length}
                        >
                          <Icon name={pickedAlreadyInvited ? 'redo' : 'paper-plane'} />
                          {' '}
                          {pickedAlreadyInvited
                            ? t('reinviteSelectedCount', { n: pickedStaged.length })
                            : t('inviteSelectedCount', { n: pickedStaged.length })}
                        </button>
                      </div>
                      {/* The answer to the click, where the click happened. */}
                      {sendNotice && (
                        <p
                          className={`bravos-cal__notice bravos-netbatch__notice${
                            sendNotice.warn ? ' bravos-cal__notice--warn' : ''}`}
                        >
                          {sendNotice.text}
                        </p>
                      )}
                    </div>
                    <span className="bravos-netform__hint bravos-netform__hint--warn">
                      {t('reinviteSelectedHint')}
                    </span>
                  </>
                )}

                {staged.map((s) => (
                  <div key={s.id} className="bravos-member bravos-member--staged">
                    <div className="bravos-member__row">
                      <input
                        type="checkbox"
                        className="bravos-netpick__cb"
                        checked={picked.includes(s.id)}
                        onChange={() => togglePick(s.id)}
                      />
                      {/* No account yet, so no photo — the initials are the fallback AND the
                          quietest signal that this row is a colleague we are still waiting on. */}
                      <Avatar
                        name={[s.first_name, s.last_name].filter(Boolean).join(' ')}
                        email={s.email}
                      />
                      <span className="bravos-member__body">
                        <span className="bravos-member__name">
                          {[s.first_name, s.last_name].filter(Boolean).join(' ') || s.email}
                        </span>
                        <span className="bravos-member__email">{s.email}</span>
                      </span>
                      <span className="bravos-member__pending">
                        {pendingLabel(s)}
                      </span>
                      <button
                        type="button"
                        className="bravos-member__makeadmin"
                        onClick={() => invitePending([s.id], { force: true })}
                        disabled={busy}
                      >
                        {s.invited_at ? t('inviteAgain') : t('invite')}
                      </button>
                      <button
                        type="button"
                        className="bravos-member__remove"
                        onClick={() => dropStaged(s)}
                        disabled={busy}
                        title={t('removeMember')}
                      >
                        <Icon name="trash" />
                      </button>
                    </div>
                    <div className="bravos-member__meta">
                      <RowTags
                        tags={s.tags || []}
                        meta={tagMeta}
                        disabled={busy}
                        onSet={(tags) => setTags(s.id, tags)}
                      />
                      {/* An address that has not joined can be put in a house too — the house
                          waits on the row and lands the moment the invite is accepted. Nothing
                          here says so, on purpose: from the admin's side it is one roster. */}
                      {houses.length > 0 && (
                        <HouseCell
                          houses={houses}
                          value={s.house_id}
                          disabled={busy}
                          onSet={(id) => setHouse([s.id], id)}
                        />
                      )}
                    </div>
                  </div>
                ))}

              </>
            )}
          </Card>
        </>
      )}
    </Base>
  );
};

export default CompanyMembersPage;
